// CalcGlobe blog — 5 starter articles for SEO / AdSense.
// Each article body is rendered as markdown-lite (paragraphs split by \n\n; h2 via "## "; h3 via "### ").

export const blogPosts = [
  {
    slug: 'emi-calculator-guide',
    title: 'How to Calculate EMI on a Home Loan — Complete Guide with Formula',
    excerpt: 'Understand the EMI formula, see worked examples for Indian home loans, and learn practical tricks to reduce your monthly payment and total interest.',
    date: '2026-04-30',
    readingTime: '7 min read',
    tag: 'Finance',
    calcId: 'emi',
    body: `
## What is an EMI?

EMI stands for **Equated Monthly Instalment** — the fixed monthly amount you pay to your lender across the entire tenure of a loan. Every EMI you pay has two components: a portion that goes towards repaying the principal (the actual amount you borrowed), and a portion that goes towards paying the interest the bank charges for the loan. The split changes month-by-month — in early EMIs most of your payment is interest, while in the last EMIs most of it goes towards principal.

This system makes loans predictable for borrowers because the EMI itself stays the same throughout the loan period. It also makes loans profitable for banks because they collect most of the interest in the early years of the loan.

## The EMI Formula

The mathematical formula used by every bank in India to calculate EMI is:

\`EMI = [P × R × (1+R)^N] / [(1+R)^N - 1]\`

Where **P** is the principal loan amount, **R** is the monthly interest rate (annual rate divided by 12 and by 100), and **N** is the loan tenure expressed in months. This compound interest formula assumes the interest is calculated on the reducing balance — which is how all home loans, car loans, and personal loans work in India today.

If you prefer not to do this by hand, our [EMI Calculator](/emi-calculator) does it instantly and also shows you a complete month-by-month amortisation schedule.

## Worked Example — ₹50 Lakh Home Loan

Let's say you are taking a home loan of ₹50,00,000 at 8.5% annual interest for 20 years. Here is how the EMI is calculated step by step.

### Step 1 — Convert the annual rate to a monthly rate
R = 8.5 ÷ 12 ÷ 100 = 0.007083 (about 0.71% per month)

### Step 2 — Convert tenure to months
N = 20 × 12 = 240 months

### Step 3 — Apply the formula
EMI = [50,00,000 × 0.007083 × (1.007083)^240] / [(1.007083)^240 - 1]
EMI ≈ **₹43,391 per month**

### Step 4 — Total cost over 20 years
Total amount paid = 43,391 × 240 = ₹1,04,13,840
Total interest paid = 1,04,13,840 − 50,00,000 = **₹54,13,840**

Notice that the total interest over 20 years is greater than the principal itself. This is why the loan tenure has such a massive impact on overall cost.

## Five Tips to Reduce Your EMI

### 1. Make a bigger down payment
The simplest way to reduce EMI is to borrow less. Every extra ₹1 lakh in down payment reduces your EMI by about ₹870 (on a 20-year loan at 8.5%) and saves you ~₹1.08 lakh in total interest.

### 2. Choose a shorter tenure
A 15-year loan has a slightly higher EMI than a 20-year loan but saves you 25–30% in total interest. If your income supports it, a shorter tenure is almost always financially better.

### 3. Negotiate the interest rate
Banks regularly offer rate concessions to existing customers, salaried employees of large companies, and women borrowers. Even a 0.25% rate cut on a ₹50 lakh loan saves you ₹3+ lakh over 20 years.

### 4. Use prepayment whenever possible
Most banks allow free prepayment on floating rate home loans. A one-time prepayment of ₹5 lakh in year 5 of a 20-year loan reduces your remaining tenure by approximately 3 years.

### 5. Switch lenders if rates fall
If the RBI cuts rates by more than 0.5% and your current bank does not pass it on, transfer your loan to another lender (called a balance transfer). The processing fee is usually recovered in less than a year.

## Fixed Rate vs Floating Rate

In India most home loans are on a **floating rate** linked to the bank's repo-linked lending rate (RLLR). The EMI changes whenever the RBI changes the repo rate. Historically, floating rates have worked out cheaper than fixed rates over long tenures because rates trend down over time on average.

A **fixed rate** loan locks in your EMI for the entire tenure. It is suitable when interest rates are at a generational low and you expect them to rise. The downside is that fixed rate loans typically charge 1–2% extra compared to equivalent floating loans.

## EMI vs Bullet Repayment

A regular EMI is a **fully amortised loan** — you finish the loan exactly at the end of the tenure with no balloon payment. A few loan products (like short-term business loans) use **bullet repayment** where you only pay interest monthly and repay the entire principal at the end. EMI-style loans are safer for most borrowers because there is no large final payment to worry about.

## Conclusion

Understanding the EMI formula is the foundation of every loan decision — whether you are buying your first home, upgrading your car, or financing higher education. Use the [CalcGlobe EMI Calculator](/emi-calculator) to model different scenarios before signing any loan agreement. A 30-minute exercise in playing with the principal, interest rate, and tenure can easily save you several lakh rupees over the life of a loan.
`,
  },

  {
    slug: 'o-ring-size-chart-guide',
    title: 'O-Ring Size Chart — Complete AS568 and ISO 3601 Guide',
    excerpt: 'Everything you need to know about O-ring standards, measurement methods, material selection, and how to find the right replacement size every time.',
    date: '2026-04-30',
    readingTime: '8 min read',
    tag: 'Engineering',
    calcId: 'oring',
    body: `
## What is an O-Ring?

An O-ring is a torus-shaped (doughnut-shaped) elastomer ring used as a mechanical gasket to seal connections between two parts. The O-ring sits in a precision-machined groove and is compressed when the parts are joined, creating a leak-proof seal that prevents fluids, gases, or pressure from escaping.

O-rings are arguably the most ubiquitous sealing component in modern mechanical engineering — you will find them in everything from car engines and hydraulic cylinders to dive watches, washing machines, and the lid of your pressure cooker. Their effectiveness, low cost, and simplicity of installation have made them the default choice for static and dynamic sealing applications since their invention in the 1930s.

## O-Ring Size Standards

Several international standards govern O-ring dimensions. Understanding which standard your equipment uses is critical before ordering a replacement.

### AS568 (American Standard)
Published by the Society of Automotive Engineers (SAE) under the title AS568A. This is the most widely used standard globally. AS568 sizes use a three-digit "dash number" notation (e.g. -214). The series covers sizes from 001 (1.78mm CS) up to 475 (large industrial diameters). Use our [O-Ring Size Chart](/o-ring-sizes) to look up any AS568 dash number instantly.

### ISO 3601 (International Standard)
The internationally recognised metric standard. ISO 3601-1 defines four series (A, B, C, G) covering different application categories. ISO sizes are specified by their nominal inner diameter and cross section in millimetres. ISO 3601 is the dominant standard in Europe and most of Asia.

### Other Standards
**BS1806** is a legacy British standard largely equivalent to AS568. **JIS B2401** is the Japanese standard with a unique series of metric sizes used in Japanese automotive and electronics equipment. **DIN 3771** is an older German standard now mostly superseded by ISO 3601.

## How to Measure an O-Ring

You only need two measurements to identify any O-ring: the **inner diameter (ID)** and the **cross-section diameter (CS)**. With those, the outer diameter is simply ID + 2 × CS.

### Step 1 — Get a digital calliper
Vernier callipers work but a digital calliper is far easier to read accurately.

### Step 2 — Measure the inner diameter
Place the O-ring on a flat surface. Use the inside jaws of the calliper to measure the inner diameter at its widest point. Take three measurements at different points and average them — used O-rings can be slightly oval.

### Step 3 — Measure the cross section
Use the outside jaws of the calliper to measure the thickness of the rubber cord. This should be uniform around the entire ring; if it varies, the ring has been compressed unevenly and likely needs replacement.

### Step 4 — Look up the size
Enter your ID and CS values into the [O-Ring Size Chart](/o-ring-sizes) to find the matching standard size.

## O-Ring Material Selection

Picking the right size is only half the battle — material selection is equally important. The wrong material can fail catastrophically when exposed to incompatible fluids or temperatures.

### NBR / Nitrile (Buna-N)
The default workhorse. Excellent compatibility with mineral oils, petroleum-based fuels, and water. Operating range: −40°C to +120°C. Used in hydraulic systems, fuel systems, and general industrial applications. Cannot be used with ozone, sunlight, or polar solvents (acetone, methanol).

### FKM / Viton (Fluorocarbon)
For high temperatures and aggressive chemicals. Operating range: −20°C to +200°C (some grades to +230°C). Resistant to most fuels, acids, and chlorinated solvents. Used in engine seals, chemical processing, and aerospace applications. Significantly more expensive than NBR — only specify it when actually needed.

### EPDM
The best choice for water, steam, brake fluid (DOT-3, DOT-4, DOT-5.1), and outdoor exposure (excellent ozone and UV resistance). Operating range: −50°C to +150°C. Common in plumbing, automotive cooling systems, and outdoor irrigation. Cannot be used with petroleum oils — they cause it to swell rapidly.

### Silicone (VMQ)
For extreme temperature ranges. Operating range: −60°C to +230°C. Used in cookware, medical devices, and electronics. Poor abrasion resistance and not suitable for dynamic seals — only static applications.

### PTFE (Teflon)
Technically not an elastomer (it doesn't bounce back) so PTFE is used in spring-energised seals or as backup rings rather than primary seals. Chemically inert — compatible with virtually everything.

## Static vs Dynamic Applications

A **static seal** is one where the two surfaces being sealed do not move relative to each other (a flange joint, a valve cover). Almost any O-ring material works in a static seal as long as the chemical compatibility is correct.

A **dynamic seal** involves relative motion between the two surfaces (a piston rod, a rotating shaft). Dynamic seals require harder durometer (typically 80–90 Shore A), tighter dimensional tolerances, and careful surface finish on the mating part. Many dynamic seal failures are not the O-ring's fault but the result of a scratched piston rod or rough cylinder bore.

## Common Causes of O-Ring Failure

**Compression set** — the O-ring has been squashed for so long it has lost its springiness and no longer seals. Fix: replace and consider a harder material.

**Chemical attack** — the O-ring is swollen, sticky, or brittle. Fix: switch to a chemically compatible material.

**Extrusion** — bits of the O-ring have been forced out of the groove by high pressure. Fix: use a backup ring or a harder durometer.

**Thermal degradation** — the ring is hardened, cracked, or charred. Fix: switch to a higher-temperature material (FKM, silicone).

**Installation damage** — small nicks or cuts during installation. Fix: lubricate the ring with a compatible grease, avoid sharp edges on the mating parts.

## Conclusion

The right O-ring is a small, cheap component that prevents very expensive failures — leaks, fires, equipment damage, and warranty claims. Take the time to specify the correct size from a recognised standard (AS568 or ISO 3601), choose a material that matches your operating conditions, and install it carefully. Our [O-Ring Size Chart](/o-ring-sizes) covers all common standard sizes with the dimensions you need to make the right choice.
`,
  },

  {
    slug: 'iv-cannula-colour-code-guide',
    title: 'IV Cannula Colour Code Chart — Complete Gauge Guide for Nurses',
    excerpt: 'A practical reference for clinical staff: cannula gauge sizes, ISO 10555 colour codes, flow rates, and the right cannula for every clinical scenario.',
    date: '2026-04-30',
    readingTime: '8 min read',
    tag: 'Medical',
    calcId: 'iv-cannula',
    body: `
## IV Cannulation — A Foundational Clinical Skill

Inserting an intravenous (IV) cannula is one of the most common procedures in modern medicine. Every nurse, doctor, paramedic, and anaesthetist performs hundreds, sometimes thousands of cannulations in their career. The skill itself can be learned in a day, but choosing the right cannula for each clinical situation requires understanding the relationship between gauge size, flow rate, vein selection, and patient comfort.

This guide walks through the ISO 10555 colour-coded standard for IV cannulas, the flow rates each size can deliver, and the decision-making framework for picking the right cannula every time. For instant size and colour lookups, see our [IV Cannula Size Chart](/iv-cannula-sizes).

## The Gauge System Explained

Cannula sizes are measured in **gauge (G)** — a counter-intuitive scale where larger numbers mean smaller cannulas. The system originated from the wire-drawing industry: a "14 gauge" wire was thicker than a "20 gauge" wire because the number referred to how many times the original wire had been drawn through progressively smaller dies.

For IV cannulas, the gauge directly affects:
- **Flow rate** — how fast fluid can pass through
- **Vein selection** — larger gauge needs larger veins
- **Patient comfort** — smaller gauge is less painful to insert
- **Insertion success rate** — smaller gauge is easier on fragile veins

## ISO 10555 Colour Code Chart

The International Standard ISO 10555 mandates a universal colour-coding scheme so that cannulas can be identified at a glance in any country and any clinical setting. This is essential in emergency situations where seconds matter.

### 14G — Orange/Brown
Outer diameter: 2.1 mm · Flow rate: ~270 mL/min · Length: 45 mm
**Clinical use:** Major trauma, massive haemorrhage, rapid blood transfusion in shock. Requires a large vein (antecubital fossa or external jugular). Painful to insert and uncomfortable for the patient — only use when clinically justified.

### 16G — Grey
Outer diameter: 1.8 mm · Flow rate: ~200 mL/min · Length: 45 mm
**Clinical use:** Major surgery, rapid fluid resuscitation, multiple unit blood transfusion. Standard for major emergency theatre cases and trauma resus.

### 18G — Green
Outer diameter: 1.3 mm · Flow rate: ~90–100 mL/min · Length: 32–45 mm
**Clinical use:** Blood transfusion (minimum recommended), routine surgery, contrast CT/MRI injections, blood product administration. The everyday workhorse in adult clinical practice.

### 20G — Pink (or Rose)
Outer diameter: 1.0 mm · Flow rate: ~54–60 mL/min · Length: 32 mm
**Clinical use:** Routine IV fluids, IV antibiotics, most IV medications, patients with smaller veins. The default cannula for stable adult ward patients who do not require blood transfusion.

### 22G — Blue
Outer diameter: 0.8 mm · Flow rate: ~31–36 mL/min · Length: 25 mm
**Clinical use:** Elderly patients with fragile veins, paediatric patients (over 1 year), most chemotherapy infusions, patients on long-term IV antibiotics. Reduced flow rate so not suitable for resuscitation.

### 24G — Yellow
Outer diameter: 0.7 mm · Flow rate: ~13–22 mL/min · Length: 19 mm
**Clinical use:** Neonates, infants, patients with very fragile veins (oncology, elderly with marked atherosclerosis). Very low flow rate; restricted to medications and slow infusions.

### 26G — Violet
Outer diameter: 0.6 mm · Flow rate: ~10–15 mL/min · Length: 19 mm
**Clinical use:** Neonatal intensive care, premature infants. Rarely seen on general adult wards.

## How to Choose the Right Cannula

A simple decision tree:

### Step 1 — What is the clinical indication?
- Resuscitation, blood transfusion, rapid IV fluids → 16G or 18G
- Routine IV fluids, medications → 20G
- Long-term antibiotics, chemo, fragile veins → 22G

### Step 2 — Assess the patient's veins
Look at the cephalic, basilic, and median cubital veins. Choose the largest, straightest, most visible vein that you can stabilise. A larger cannula in a small vein is more likely to fail than a smaller cannula in a large vein.

### Step 3 — Consider patient comfort
For non-emergency situations, always select the smallest gauge that meets the clinical requirement. A 20G cannula causes significantly less pain on insertion than an 18G, and a 22G is more comfortable for the patient for the duration of the IV therapy.

### Step 4 — Plan for duration
If you anticipate the patient will need IV access for more than 72 hours, consider whether a peripherally inserted central catheter (PICC) or midline catheter is more appropriate. Peripheral cannulas should be replaced every 72–96 hours per CDC and NHS guidelines.

## Why Flow Rate Matters

The flow rate of a cannula is governed by **Poiseuille's law**:

\`Flow rate = (π × ΔP × r^4) / (8 × η × L)\`

The critical insight is that flow rate is proportional to the **fourth power** of the radius. Doubling the radius increases flow by 16 times. This is why:

- For massive fluid resuscitation, a single 14G cannula moves fluid faster than two 18G cannulas combined.
- For routine IV fluid administration, the difference between 18G and 20G is negligible (90 vs 54 mL/min — both fast enough for routine therapy).
- Length matters too — a shorter cannula has higher flow rate than a longer one of the same gauge.

## Common Cannulation Pitfalls

### Choosing too large a cannula "to be safe"
A 16G in an elderly patient's fragile vein will most likely fail on insertion or infiltrate within 12 hours. Match the size to the vein.

### Not flushing after insertion
Always flush with 5–10 mL of sterile saline after insertion to confirm patency and prevent clotting. A cannula that flushes freely will deliver medications reliably; one that meets resistance has either kinked, dislodged, or is in tissue rather than the vein.

### Inadequate fixation
The most common reason cannulas fail is dislodgement. Use a transparent dressing (Tegaderm or similar), document the insertion date, and check the site every shift for signs of phlebitis (the VIP score: visual infusion phlebitis).

### Reusing the same site
If a cannulation attempt fails, choose a different vein — preferably more proximal — for the next attempt. Re-cannulating the same vein increases the risk of haematoma, phlebitis, and infection.

## Conclusion

The right cannula is the smallest one that does the job. Master the ISO 10555 colour code, understand the relationship between gauge and flow rate, and always match cannula size to both the clinical indication and the patient's vein anatomy. For instant size and colour lookups, bookmark our [IV Cannula Size Chart](/iv-cannula-sizes).
`,
  },

  {
    slug: 'sip-calculator-guide',
    title: 'SIP Calculator — How Much to Invest Monthly to Reach ₹1 Crore',
    excerpt: 'Use SIP investing to systematically build a ₹1 crore corpus. Realistic return scenarios, monthly investment targets, and mutual fund selection tips.',
    date: '2026-04-30',
    readingTime: '7 min read',
    tag: 'Finance',
    calcId: 'sip',
    body: `
## The SIP Revolution in Indian Investing

A Systematic Investment Plan (SIP) is a method of investing in mutual funds where you commit to a fixed monthly contribution — typically debited automatically from your bank account on a chosen date each month. SIP investing has transformed how middle-class India saves: from a country where the typical saver kept everything in fixed deposits and physical gold, to one where mutual fund assets crossed ₹50 lakh crore in 2024 — driven primarily by retail SIP flows of over ₹20,000 crore every single month.

The reason SIP works so well for ordinary investors is not magic. It is a combination of three financial principles working simultaneously: **disciplined saving**, **rupee cost averaging**, and **compounding**. Use our [SIP Calculator](/sip-calculator) to model your own goals.

## How Compounding Builds a Crore

The single most important financial concept for a young investor to understand is compound growth. When your investment earns a return, that return itself starts earning returns the following year — a phenomenon Albert Einstein supposedly called "the eighth wonder of the world."

Consider three investors, all targeting a ₹1 crore corpus:

### Aarav — starts at age 25
Monthly SIP: ₹5,000 · Duration: 25 years · Expected return: 12% p.a.
At age 50: corpus ≈ **₹95 lakh**. Total invested: only ₹15 lakh.

### Priya — starts at age 30
Monthly SIP: ₹10,000 · Duration: 20 years · Expected return: 12% p.a.
At age 50: corpus ≈ **₹1.0 crore**. Total invested: ₹24 lakh.

### Rohan — starts at age 35
Monthly SIP: ₹22,000 · Duration: 15 years · Expected return: 12% p.a.
At age 50: corpus ≈ **₹1.1 crore**. Total invested: ₹39.6 lakh.

All three reach approximately the same corpus, but Rohan had to invest **2.6 times more** money than Aarav because he started 10 years later. The cost of delay is staggering.

## Realistic Return Assumptions

When using a SIP calculator, the most important input — and the most commonly overestimated — is the expected return. Here is a guide to realistic numbers based on long-term historical data from Indian mutual funds.

### Equity (large-cap and flexi-cap funds): 11–13% CAGR
Over rolling 15-year periods since 1999, broad Indian equity indices have delivered around 12% CAGR. Individual fund performance varies but a well-diversified equity fund should average in this range over the long term.

### Equity (small-cap and mid-cap funds): 12–15% CAGR
Higher growth potential but with significantly higher volatility. Suitable only for investors with a long horizon (10+ years) and the temperament to ride out 30–40% drawdowns.

### Hybrid (balanced advantage / multi-asset): 9–11% CAGR
Lower volatility than pure equity. Suitable for investors closer to their goal or with lower risk tolerance.

### Debt funds: 6–8% CAGR
Comparable to or slightly better than fixed deposits with tax efficiency advantages. Used for short-term goals (under 3 years).

For long-term planning use **12%** as a base case, **10%** as a conservative estimate, and **15%** only as an optimistic scenario.

## The Three Stages of an SIP Journey

### Stage 1 — Accumulation (Years 1–7)
In the early years your contributions far exceed your returns. The corpus feels like it is barely growing. This is normal and expected. The discipline of monthly investing builds the habit and accumulates the base capital.

### Stage 2 — Growth (Years 7–15)
Returns start to compound meaningfully. The corpus growth accelerates. At this point, a 10% market correction can erase 1–2 years of contributions, but your continued SIP means you are now buying at a discount.

### Stage 3 — Compounding Magic (Years 15+)
Annual returns now exceed your annual contributions. The corpus growth becomes exponential. This is when the "crorepati" milestone is hit for those who started early.

## SIP Step-Up — The Wealth Accelerator

A "step-up SIP" increases your monthly contribution by a fixed percentage each year, typically tied to your salary increment. The impact is dramatic.

### Without step-up
₹10,000/month for 20 years at 12% = ₹1.0 crore. Total invested: ₹24 lakh.

### With 10% annual step-up
Starting at ₹10,000/month, stepping up 10% each year, for 20 years at 12% = **₹2.05 crore**. Total invested: ₹68.7 lakh.

A 10% annual step-up roughly doubles your final corpus because the additional contributions in later years compound for 5–10+ years. Most mutual fund apps now support automatic step-up — enable this feature if you can.

## How to Pick the Right Mutual Fund for SIP

### 1. Check the long-term performance
Look at 10-year and 15-year CAGR. Short-term winners often become long-term losers.

### 2. Look at consistency, not peaks
A fund that delivered 14% for ten consecutive years is usually a better choice than one that delivered 25% three times and −5% twice.

### 3. Consider the expense ratio
A direct-plan equity fund typically has an expense ratio of 0.5–1.0%. Avoid regular plans (with distributor commissions) which charge 1.5–2.5%. Over 25 years a 1% extra expense ratio costs you approximately 22% of your final corpus.

### 4. Diversify across categories
Don't put everything in one fund. A typical SIP allocation: 50–60% in flexi-cap or large-cap, 20–30% in mid/small-cap, 10–20% in international or sectoral funds.

### 5. Use direct plans on apps like Groww, Zerodha Coin, Kuvera, or MFCentral
These platforms charge no commission and allow you to invest in direct mutual fund plans — saving you significant money over the long term.

## Common SIP Mistakes to Avoid

**Stopping SIP during market downturns.** When markets fall, SIP buys more units for the same money. This is when compounding is being set up — the absolute worst time to stop.

**Switching funds frequently.** Performance over a 6-month period tells you almost nothing. Give a fund at least 3 years before evaluating.

**Investing without a goal.** "Make money" is not a goal. Define the target corpus, target date, and required monthly SIP from a calculator like ours.

**Confusing returns with absolute amounts.** "I made ₹50,000 on a ₹5 lakh investment in 6 months" sounds great, but that's only a 20% annualised return — and it might be down 30% next quarter.

## Conclusion

SIP is the closest thing to a "secret weapon" available to retail investors in India. It removes the need to time the market, leverages the power of compounding, and creates wealth through disciplined consistency rather than brilliance. The most important decision is to start — even a small SIP started today will outperform a large SIP started five years from now. Use the [SIP Calculator](/sip-calculator) to find your numbers and begin.
`,
  },

  {
    slug: 'calorie-calculator-guide',
    title: 'How Many Calories Should I Eat Per Day — TDEE Calculator Guide',
    excerpt: 'Calculate your daily calorie target using the Mifflin-St Jeor equation, choose the right macro split, and turn the number into a real sustainable diet.',
    date: '2026-04-30',
    readingTime: '7 min read',
    tag: 'Health',
    calcId: 'calorie',
    body: `
## The One Number That Drives Every Diet

Whether you want to lose 10 kg, gain muscle, or simply maintain a healthy weight without thinking about food, every diet decision flows from one number: your **Total Daily Energy Expenditure (TDEE)** — the number of calories your body burns in 24 hours.

Eat less than this number and you lose weight. Eat more and you gain weight. Eat exactly this number and you stay the same. Every popular diet — keto, intermittent fasting, paleo, vegan, carnivore — works only to the extent that it puts you in the right calorie state. None of them magically defeat the laws of thermodynamics.

Use our [Calorie / TDEE Calculator](/calorie-tdee-calculator) to find your number, then read on to understand what to do with it.

## How TDEE Is Calculated

Your TDEE is your Basal Metabolic Rate (BMR) multiplied by an activity factor.

### Basal Metabolic Rate (BMR)
BMR is the energy your body burns at complete rest just to keep you alive — heartbeat, breathing, brain function, cellular maintenance. It represents 60–70% of your TDEE for most people.

The most accurate formula for healthy adults is the **Mifflin-St Jeor equation**, validated in numerous metabolic studies:

**Men:** BMR = (10 × weight in kg) + (6.25 × height in cm) − (5 × age in years) + 5
**Women:** BMR = (10 × weight in kg) + (6.25 × height in cm) − (5 × age in years) − 161

### Activity Multiplier
Once you have BMR, you multiply by an activity factor:
- **1.2** — Sedentary (desk job, little or no exercise)
- **1.375** — Lightly active (light exercise 1–3 days/week)
- **1.55** — Moderately active (exercise 3–5 days/week)
- **1.725** — Very active (hard exercise 6–7 days/week)
- **1.9** — Extremely active (physical job + hard training, athlete)

## Worked Example

A 35-year-old man, 178 cm tall, 80 kg, with a desk job who works out 4 times a week:

**Step 1 — BMR**
BMR = (10 × 80) + (6.25 × 178) − (5 × 35) + 5
BMR = 800 + 1,112.5 − 175 + 5 = **1,742.5 calories**

**Step 2 — Activity multiplier**
Moderately active = 1.55

**Step 3 — TDEE**
TDEE = 1,742.5 × 1.55 = **≈ 2,701 calories/day**

This is his maintenance calorie target. From here he can set diet goals:
- **Lose 0.5 kg/week:** 2,701 − 500 = 2,201 calories/day
- **Lose 1 kg/week:** 2,701 − 1,000 = 1,701 calories/day (aggressive)
- **Maintain:** 2,701 calories/day
- **Gain muscle (lean bulk):** 2,701 + 300 = 3,001 calories/day

## Setting the Right Calorie Goal

### For weight loss
A deficit of 500 calories per day produces approximately 0.5 kg of fat loss per week. This is the sweet spot for most people: aggressive enough to see weekly progress, but small enough to be sustainable without destroying your energy levels.

Deficits larger than 750 calories per day are risky for most people. They lead to muscle loss, metabolic adaptation, severe fatigue, and rebound eating. They are only justified under medical supervision for clinically obese individuals.

### For muscle gain
A surplus of 200–400 calories per day combined with progressive resistance training produces approximately 0.25–0.5 kg of lean mass gain per month for a beginner. Larger surpluses just produce more fat gain without faster muscle growth — there is a biological limit to how fast new muscle can be built.

### For maintenance
Eat at your TDEE. Weigh yourself weekly and adjust by ±100–150 calories if your weight drifts in an unwanted direction over a 4-week period.

## Macro Splitting — Beyond Just Calories

Once you have your calorie target, the next step is to split those calories across the three macronutrients: protein, fat, and carbohydrate.

### Protein — the most important macro
**Target: 1.6–2.2 grams per kg of body weight**. Protein has the highest thermic effect (your body burns 25–30% of protein calories just digesting it), the strongest satiety effect, and is essential for preserving muscle in a calorie deficit.

For an 80 kg person, that's 128–176 grams of protein per day — roughly 4 protein-rich meals (chicken breast, eggs, dal, paneer, fish, whey protein).

### Fat — for hormones and satiety
**Target: 0.6–1.0 grams per kg of body weight**, or about 25–30% of total calories. Fat is essential for hormone production (especially testosterone and oestrogen). Drop fat below 20% of calories and many people see a drop in mood and libido.

### Carbohydrates — fuel for performance
**Whatever calories remain after protein and fat.** Carbs are the easiest macro to adjust up or down based on energy needs and activity level. Active individuals do well on higher carbs (40–55% of calories); sedentary individuals or those with insulin resistance often feel better on lower carbs (20–35%).

## Why People Fail at Calorie Counting

### Reason 1 — They underestimate intake
Studies show that people underestimate their actual food intake by 20–40% on average. The fix is to weigh food on a kitchen scale for at least 2 weeks until you can accurately eyeball portions.

### Reason 2 — They overestimate exercise calories
A "1-hour gym session" rarely burns more than 300–400 calories for a normal-sized person. Most fitness trackers overestimate exercise calorie burn by 50–100%. Don't add exercise calories on top of an activity-adjusted TDEE.

### Reason 3 — They quit too early
Weight loss is not linear. You can be in a perfect deficit for two weeks and see zero scale movement due to water retention, glycogen fluctuation, or menstrual cycle changes. Trust the math — if you've eaten in a deficit for 4 weeks, you have lost fat, even if the scale hasn't moved much yet.

### Reason 4 — They set unsustainable goals
Going from 2,800 calories/day to 1,400 calories/day overnight is doomed to fail. Reduce calories by 300–500 from current intake, settle there for 2 weeks, then assess.

## Conclusion

The TDEE calculator is the most important first step in any health, weight loss, or fitness journey. Get your number, set a target based on your goal, hit your protein, and stay consistent for at least 12 weeks before evaluating. There is no magic — just math, applied patiently. Run your numbers with our [Calorie / TDEE Calculator](/calorie-tdee-calculator) and start today.
`,
  },
];

export const blogPostBySlug = Object.fromEntries(blogPosts.map(p => [p.slug, p]));
